import { ANALYTICS_EVENTS } from '@dunea/shared/analytics/events';
import { capture } from '~/lib/posthog';
import type { DirectEditResult } from './direct-edit';

export type EditSurface = 'preview' | 'canvas';
export type VisualEditType = 'text' | 'style' | 'image' | 'restore';
export type VisualEditResult = 'applied' | 'failed' | 'sent_to_ai';

// One Canvas Mode session at a time; reset on open, read on close.
let canvasOpenedAt: number | null = null;
let canvasEdits = 0;

export function trackCanvasOpened() {
  canvasOpenedAt = Date.now();
  canvasEdits = 0;
  capture(ANALYTICS_EVENTS.CANVAS_OPENED);
}

export function trackCanvasClosed() {
  if (canvasOpenedAt === null) {
    return;
  }

  capture(ANALYTICS_EVENTS.CANVAS_CLOSED, {
    seconds: Math.round((Date.now() - canvasOpenedAt) / 1000),
    edits: canvasEdits,
  });
  canvasOpenedAt = null;
}

export function trackElementSelected(surface: EditSurface, tag: string) {
  capture(ANALYTICS_EVENTS.ELEMENT_SELECTED, { surface, tag });
}

/** `fallback` is the result to record when the direct edit fails. */
export function trackVisualEdit(
  surface: EditSurface,
  type: VisualEditType,
  outcome: DirectEditResult,
  fallback: Exclude<VisualEditResult, 'applied'> = 'failed',
) {
  const result: VisualEditResult = outcome.ok ? 'applied' : fallback;

  if (surface === 'canvas' && outcome.ok) {
    canvasEdits++;
  }

  capture(ANALYTICS_EVENTS.VISUAL_EDIT, {
    surface,
    type,
    result,
    ...(outcome.ok ? {} : { reason: outcome.reason }),
  });
}

export function trackSelectionSentToChat(kind: 'prompt' | 'delete') {
  capture(ANALYTICS_EVENTS.SELECTION_SENT_TO_CHAT, { kind });
}
